import type { DocId, Edges, MapNode, NodeId, NodeMap, Permissions } from './types';
import { DOC_ID_FIELD_BY_KEY, DOC_KINDS } from './docs';
import { canAiAdmin, canEditAllDocs } from './permissions';

/**
 * The RESPONSIBILITY resolver, client half (V0.37) — the pure mirror of
 * `server/src/auth/responsibility.ts`, computed over what the map payload and the workflows
 * fetch actually hand this client. The server is the enforcement; these answers decide which
 * affordances are DRAWN, so the two must give the same answer over the same data.
 *
 * WHAT THE DATA IS, AND WHAT THAT MAKES POSSIBLE:
 *
 *  - `MapNode.responsibles` — the DIRECT NodeResponsible rows, already eligibility-filtered
 *    AND group-expanded server-side (a member of an assigned team arrives as a plain user id;
 *    a dormant assignment never arrives), shipped to every PARTICIPANT. Inheritance is
 *    resolved HERE, by walking ancestors — the same union-down-the-tree rule the server's
 *    resolver applies, over the same rows.
 *  - `docWorkflow[docId].responsibles` — the eligible DOC responsibles per page, for the
 *    entry's RESOLVED locale (`GET /api/docs/workflows` resolves the requested → source
 *    fallback server-side and says which locale each entry is about). Groups are expanded to
 *    their eligible members server-side here too, which is why matching MY id against the
 *    list answers team-borne assignment with ZERO group state on this client — deliberate:
 *    memberships are not in `/api/auth/me`. A doc-level answer about any other locale is
 *    honestly "unknown" and is answered `false`.
 *
 * "NO DATA YET IS NO": every predicate answers `false` until the state it reads has arrived —
 * no session, an empty map, a workflows fetch still in flight. A gate that guessed `true`
 * would draw an affordance whose write the server then refuses, which is the exact failure
 * the affordance sweep exists to remove.
 */

/** The slice of app state these predicates read. `AppSnapshot` satisfies it structurally. */
export interface ResponsibilityState {
  perm: Permissions | null;
  currentUser: { id: string } | null;
  nodes: NodeMap;
  edges: Edges;
  docWorkflow: Record<string, { locale?: string; responsibles?: readonly string[] }>;
  docWorkflowLoaded: boolean;
  currentLocale: string;
}

const ANCESTOR_GUARD = 10_000; // the map is user-editable; a cycle must not hang the client

/**
 * The EFFECTIVE responsibles of a node: the union of the direct rows on the node and on every
 * ancestor. The walk is `isPermHidden`'s shape — child → parent up `edges` — because the
 * client only ever asks about one node at a time; the server walks top-down only so a future
 * NodeResponsibleBlock is a cheap insertion, and until that exists the two walks are the same
 * function.
 */
export function effectiveResponsibles(nodeId: NodeId, nodes: NodeMap, edges: Edges): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let cur: NodeId | undefined = nodeId;
  let guard = 0;
  while (cur !== undefined && guard++ < ANCESTOR_GUARD) {
    for (const userId of nodes[cur]?.responsibles ?? []) {
      if (!seen.has(userId)) {
        seen.add(userId);
        out.push(userId);
      }
    }
    cur = edges[cur];
  }
  return out;
}

/**
 * Which node CONTRIBUTES each effective responsible — the NEAREST one, walking up from the
 * node itself. What the popup's read shape renders "inherited from X" with: an entry whose
 * source is the node itself is a direct assignment, any other source is the ancestor it
 * flowed down from.
 */
export function responsibleSources(
  nodeId: NodeId,
  nodes: NodeMap,
  edges: Edges,
): Map<string, NodeId> {
  const out = new Map<string, NodeId>();
  let cur: NodeId | undefined = nodeId;
  let guard = 0;
  while (cur !== undefined && guard++ < ANCESTOR_GUARD) {
    for (const userId of nodes[cur]?.responsibles ?? []) {
      if (!out.has(userId)) out.set(userId, cur);
    }
    cur = edges[cur];
  }
  return out;
}

/** Is the signed-in user an effective responsible of this node (direct or inherited)? */
export function isResponsibleFor(s: ResponsibilityState, nodeId: NodeId): boolean {
  const me = s.currentUser?.id;
  if (!me) return false;
  let cur: NodeId | undefined = nodeId;
  let guard = 0;
  while (cur !== undefined && guard++ < ANCESTOR_GUARD) {
    if (s.nodes[cur]?.responsibles?.includes(me)) return true;
    cur = s.edges[cur];
  }
  return false;
}

/** Every doc id a node carries: the four fixed slots plus the custom list. */
function docsOf(n: MapNode): DocId[] {
  const out: DocId[] = [];
  for (const k of DOC_KINDS) {
    const id = n[DOC_ID_FIELD_BY_KEY[k.key]] as DocId | null | undefined;
    if (id) out.push(id);
  }
  for (const id of n.customDocs ?? []) out.push(id);
  return out;
}

/** Is the user an effective node responsible of ANY node that carries this doc? */
function responsibleOfOwningNode(s: ResponsibilityState, docId: DocId): boolean {
  if (!s.currentUser?.id) return false;
  for (const id in s.nodes) {
    if (docsOf(s.nodes[id]).includes(docId) && isResponsibleFor(s, id)) return true;
  }
  return false;
}

/**
 * Is the user an assigned DOC responsible for (doc, locale)? Only answerable for the locale
 * the entry actually RESOLVED to — the workflows payload carries one locale per doc (the
 * requested one, or its source-locale fallback), so any other locale is `false`, honestly,
 * because the data for it never arrives on this client (plan trap #6's accepted latency has
 * a spatial twin). The list is group-expanded server-side, so `includes(me)` answers
 * team-borne assignment with no group state here — see the header.
 */
function isDocResponsible(s: ResponsibilityState, docId: DocId, locale: string): boolean {
  const me = s.currentUser?.id;
  if (!me || !s.docWorkflowLoaded) return false;
  const entry = s.docWorkflow[docId];
  if (!entry || (entry.locale ?? s.currentLocale) !== locale) return false;
  return !!entry.responsibles?.includes(me);
}

/**
 * `mayEditDoc(doc, locale)`, mirrored: EDIT_ALL_DOCS ∨ assigned doc responsible for this
 * locale ∨ effective node responsible of an owning node.
 */
export function canEditDoc(s: ResponsibilityState, docId: DocId, locale: string): boolean {
  if (!s.currentUser) return false;
  if (canEditAllDocs(s.perm)) return true;
  return isDocResponsible(s, docId, locale) || responsibleOfOwningNode(s, docId);
}

/** === `canEditDoc`, still — V0.38's verbs kept the workflow scoped to exactly the people
 *  who may edit the page (`mayActOnWorkflow` is the same alias server-side). The separate
 *  name survives so the day the scopes diverge is a one-line change at both ends. */
export const canActOnWorkflow = canEditDoc;

/**
 * The RESPONSIBILITY-scoped half of `mayEditPrompt` — the server's `promptVisibleNodeIds`:
 * effective node responsible, or doc responsible of one of the node's docs (current locale).
 * The role overrides are deliberately NOT folded in; `canEditPrompt` below adds them, and the
 * node popup's AI panel wants exactly this half (an AI admin's prompt surface stays the
 * AI-doc mode it has always been, or every macro editor's popup would change shape).
 */
export function isPromptResponsible(s: ResponsibilityState, nodeId: NodeId): boolean {
  if (!s.currentUser) return false;
  if (isResponsibleFor(s, nodeId)) return true;
  const n = s.nodes[nodeId];
  if (!n) return false;
  return docsOf(n).some((docId) => isDocResponsible(s, docId, s.currentLocale));
}

/**
 * `mayEditPrompt(node)`, mirrored in full: AI_ADMIN ∨ EDIT_ALL_DOCS ∨ the responsibility
 * scope above. This is what decides whether an aiPrompt write can be enqueued at all — the
 * responsible-AI body family on `PUT /api/nodes/:id` is aiPrompt-only since V0.38; note the
 * AI_ADMIN half is the ROLE alone — the server accepts the field with no capability gate,
 * unlike the generation mechanics.
 */
export function canEditPrompt(s: ResponsibilityState, nodeId: NodeId): boolean {
  if (!s.currentUser) return false;
  if (canAiAdmin(s.perm) || canEditAllDocs(s.perm)) return true;
  return isPromptResponsible(s, nodeId);
}

/**
 * `mayAssignNode(node)`, mirrored: EDIT_ALL_DOCS ∨ effective responsible of the node —
 * delegation down one's own subtree falls out of inheritance, exactly as it does server-side.
 */
export function canAssign(s: ResponsibilityState, nodeId: NodeId): boolean {
  if (!s.currentUser) return false;
  return canEditAllDocs(s.perm) || isResponsibleFor(s, nodeId);
}

/**
 * `mayAssignDoc(doc)`, mirrored: EDIT_ALL_DOCS ∨ effective node responsible of an owning
 * node — distributing a document is the owning branch's authority, so a doc responsible
 * cannot re-delegate their own page. What gates the WorkflowControl popover's picker (V0.38).
 */
export function canAssignDoc(s: ResponsibilityState, docId: DocId): boolean {
  if (!s.currentUser) return false;
  return canEditAllDocs(s.perm) || responsibleOfOwningNode(s, docId);
}

/** The shape `coveredByGroup` needs of a group-directory row — structurally satisfied by
 * `app/state.ts`'s `DirectoryGroup`; declared here so the pure core keeps importing nothing
 * from the app layer. */
export interface GroupDirectoryEntry {
  readonly id: string;
  readonly name: string;
  readonly memberIds: readonly string[];
}

/**
 * Ids covered by a directly-assigned group, mapped to the (first) group's name — the same
 * membership the server expanded into `MapNode.responsibles`. The popup uses it to (a) seed
 * its individual draft WITHOUT the group's own members, so removing the group later does not
 * leave them behind as accidental individual rows, and (b) label a covered entry "via X" in
 * the read-only view.
 *
 * OVER AN EMPTY (not-yet-loaded) DIRECTORY THIS COVERS NOBODY — every group member would pass
 * the filter and land in the draft as an individual. That is why the popup refuses to trust
 * its seed until both directories have loaded (`respSeedTrusted` in NodePopup.tsx) and omits
 * the responsibles families from a save until then; the test suite pins the empty-directory
 * behaviour so that guard's reason stays visible.
 */
export function coveredByGroup(
  groupIds: readonly string[],
  groupDirectory: readonly GroupDirectoryEntry[],
): Map<string, string> {
  const out = new Map<string, string>();
  for (const gid of groupIds) {
    const g = groupDirectory.find((x) => x.id === gid);
    if (!g) continue;
    for (const uid of g.memberIds) if (!out.has(uid)) out.set(uid, g.name);
  }
  return out;
}
